import { useClient } from '@cortex/react-query';
import type {
  Evaluation,
  GroundTruth,
  ModelConfig,
  Suite,
  Task,
  WorkflowListItem,
  WorkflowVersion,
} from '@cortex/types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMemo, useState, type CSSProperties } from 'react';

import { BackButton } from '../../components/common/BackButton';
import { InfoHint } from '../../components/common/InfoHint';
import { PageHeader } from '../../components/common/PageHeader';
import { useDocumentSets } from '../../hooks/useDocumentSets';
import { routes } from '../../lib/constants';

export const Route = createFileRoute('/evals/new')({
  component: NewEvaluationPage,
  // Deep-link support: "Run to verify" from a run's feedback lands here with the
  // just-minted suite (and its workflow) preselected.
  validateSearch: (search: Record<string, unknown>): { suiteId?: string; workflowId?: string } => ({
    suiteId: typeof search.suiteId === 'string' ? search.suiteId : undefined,
    workflowId: typeof search.workflowId === 'string' ? search.workflowId : undefined,
  }),
});

const stack3: CSSProperties = { display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' };
const grid2: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
  gap: 'var(--sp-3)',
};

interface IListEnvelope<T> {
  items: T[];
  total: number;
}

type EvalMode = 'suite' | 'tasks' | 'legacy';

// ─── Mode Descriptions ──────────────────────────────────────────────────────

const modeDescriptions: Record<EvalMode, { label: string; description: string }> = {
  suite: {
    label: 'Run a Suite',
    description: 'Run all Tasks in a Suite. Best for regression testing and capability benchmarks.',
  },
  tasks: {
    label: 'Pick specific Tasks',
    description: 'Choose individual Tasks to evaluate. Best for targeted testing.',
  },
  legacy: {
    label: 'Legacy (GT sets)',
    description:
      'Score against ground truth entries directly. Original evaluation method with deterministic/LLM grading.',
  },
};

// ─── reportKs helpers ────────────────────────────────────────────────────────

/** The full reliability range [1, 2, …, n]. */
function rangeKs(n: number): number[] {
  return Array.from({ length: Math.max(1, n) }, (_, i) => i + 1);
}

/** True when ks is exactly [1, 2, …, ks.length] — i.e. an untouched full range. */
function isContiguousFromOne(ks: number[]): boolean {
  return ks.length > 0 && ks.every((k, i) => k === i + 1);
}

// ─── Chip Input for reportKs ─────────────────────────────────────────────────

function ChipInput({
  values,
  onChange,
  max,
  placeholder,
}: {
  values: number[];
  onChange: (v: number[]) => void;
  max: number;
  placeholder?: string;
}) {
  const [draft, setDraft] = useState('');

  const add = (raw: string) => {
    const n = parseInt(raw.trim(), 10);
    if (isNaN(n) || n < 1 || n > max || values.includes(n)) return;
    onChange([...values, n].sort((a, b) => a - b));
    setDraft('');
  };

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center' }}>
      {values.map((v) => (
        <span
          key={v}
          className="badge"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            padding: '2px 8px',
            fontSize: 12,
            background: 'var(--surface-2)',
            border: '1px solid var(--border-subtle)',
            borderRadius: 'var(--radius-sm)',
          }}
        >
          k={v}
          <button
            type="button"
            onClick={() => onChange(values.filter((x) => x !== v))}
            style={{
              border: 'none',
              background: 'none',
              color: 'var(--text-muted)',
              cursor: 'pointer',
              padding: 0,
              fontSize: 12,
              lineHeight: 1,
            }}
          >
            x
          </button>
        </span>
      ))}
      <input
        type="text"
        inputMode="numeric"
        className="input"
        style={{ width: 64, flex: 'none' }}
        value={draft}
        placeholder={placeholder ?? 'k'}
        onChange={(e) => setDraft(e.target.value.replace(/\D/g, ''))}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ',') {
            e.preventDefault();
            add(draft);
          }
        }}
        onBlur={() => {
          if (draft) add(draft);
        }}
      />
    </div>
  );
}

// ─── Main Page ───────────────────────────────────────────────────────────────

function NewEvaluationPage() {
  const client = useClient();
  const qc = useQueryClient();
  const nav = useNavigate();

  const { suiteId: initialSuiteId, workflowId: initialWorkflowId } = Route.useSearch();

  // ── Shared state ──
  const [workflowId, setWorkflowId] = useState(initialWorkflowId ?? '');
  const [workflowVersionId, setWorkflowVersionId] = useState('');
  const [mode, setMode] = useState<EvalMode>('suite');

  // ── Suite mode ──
  const [suiteId, setSuiteId] = useState(initialSuiteId ?? '');

  // ── Tasks mode ──
  const [selectedTaskIds, setSelectedTaskIds] = useState<string[]>([]);
  const [taskSearch, setTaskSearch] = useState('');
  const [taskPickerOpen, setTaskPickerOpen] = useState(false);

  // ── Trial settings (Suite + Tasks modes) ──
  const [nTrials, setNTrials] = useState(3);
  // Default to the full 1..nTrials range so the reliability curve renders a
  // real pass@k / pass^k curve out of the box (a lone k=1 is degenerate:
  // pass@1 === pass^1). Kept in sync as nTrials changes below.
  const [reportKs, setReportKs] = useState<number[]>(() => rangeKs(3));
  const [concurrency, setConcurrency] = useState(4);
  // Free-form labels (comma-separated) for grouping/filtering on the Evals
  // overview — e.g. "intel" to slice a customer's runs together.
  const [tagsInput, setTagsInput] = useState('');

  // ── Legacy mode ──
  const [evaluationMethod, setEvaluationMethod] = useState<'deterministic' | 'llm-judge' | 'both'>(
    'deterministic',
  );
  const [graderModelConfigId, setGraderModelConfigId] = useState('');

  const needsGrader = evaluationMethod === 'llm-judge' || evaluationMethod === 'both';

  // ── KG target state (Unify #7): only surfaced when derived kind is KG. ──
  // Suite mode with a KG Suite needs an explicit doc-set pick (the console
  // can't cheaply enumerate the Suite's tasks). Tasks mode derives the
  // doc-set from the selected tasks themselves.
  const [kgDocumentSetId, setKgDocumentSetId] = useState('');
  const [kgVersionPinId, setKgVersionPinId] = useState('');

  // ── Queries ──
  const workflows = useQuery({
    queryKey: ['workflows', 'all-for-eval'],
    queryFn: () =>
      client.http.get('v1/workflows?limit=200').json<IListEnvelope<WorkflowListItem>>(),
  });

  const versions = useQuery({
    queryKey: ['workflow-versions', workflowId],
    enabled: workflowId.length > 0,
    queryFn: () =>
      client.http
        .get(`v1/workflows/${encodeURIComponent(workflowId)}/versions`)
        .json<WorkflowVersion[]>(),
  });

  const suites = useQuery({
    queryKey: ['suites', 'all-for-eval'],
    enabled: mode === 'suite',
    queryFn: () => client.http.get('v1/suites?limit=200').json<IListEnvelope<Suite>>(),
  });

  const tasks = useQuery({
    queryKey: ['tasks', 'all-for-eval'],
    enabled: mode === 'tasks',
    queryFn: () => client.http.get('v1/tasks?limit=200').json<IListEnvelope<Task>>(),
  });

  const models = useQuery({
    enabled: mode === 'legacy' && needsGrader,
    queryKey: ['model-configs', 'all-for-eval-grader'],
    queryFn: () => client.http.get('v1/model-configs?limit=200').json<IListEnvelope<ModelConfig>>(),
  });

  const gtCount = useQuery({
    queryKey: ['ground-truth', 'count', workflowId],
    enabled: mode === 'legacy' && workflowId.length > 0,
    queryFn: () =>
      client.http
        .get(`v1/ground-truth?workflowId=${encodeURIComponent(workflowId)}&limit=1`)
        .json<IListEnvelope<GroundTruth>>(),
  });

  // ── Derived ──
  const total = gtCount.data?.total ?? 0;
  const noGroundTruth =
    mode === 'legacy' && workflowId.length > 0 && !gtCount.isLoading && total === 0;

  const selectedSuite = suites.data?.items.find((s) => s.id === suiteId);
  const taskList = tasks.data?.items ?? [];
  const availableTasks = taskList.filter((t) => !selectedTaskIds.includes(t.id));
  const selectedTasks = taskList.filter((t) => selectedTaskIds.includes(t.id));
  const filteredTasks = taskSearch
    ? availableTasks.filter(
        (t) =>
          t.name.toLowerCase().includes(taskSearch.toLowerCase()) ||
          (t.description ?? '').toLowerCase().includes(taskSearch.toLowerCase()),
      )
    : availableTasks;

  // ── Derive evalTargetKind from selection (Unify #7) ──
  // Legacy mode is always workflow-target. Suite mode reads targetKind off
  // the selected Suite. Tasks mode adopts a non-WORKFLOW kind only when
  // every selected task shares it — mixed selections are blocked in
  // canSubmit below.
  const selectedTaskKinds = new Set(selectedTasks.map((t) => t.targetKind ?? 'WORKFLOW'));
  const selectedTasksKgAll =
    selectedTasks.length > 0 && selectedTaskKinds.size === 1 && selectedTaskKinds.has('KG');
  const selectedTasksMemoryAll =
    selectedTasks.length > 0 && selectedTaskKinds.size === 1 && selectedTaskKinds.has('MEMORY');
  const selectedTasksMixed = selectedTaskKinds.size > 1;

  let evalTargetKind: 'WORKFLOW' | 'KG' | 'MEMORY' = 'WORKFLOW';
  if (mode === 'suite' && selectedSuite?.targetKind === 'KG') evalTargetKind = 'KG';
  if (mode === 'suite' && selectedSuite?.targetKind === 'MEMORY') evalTargetKind = 'MEMORY';
  if (mode === 'tasks' && selectedTasksKgAll) evalTargetKind = 'KG';
  if (mode === 'tasks' && selectedTasksMemoryAll) evalTargetKind = 'MEMORY';

  // Doc-sets are needed only when the eval is KG. Fetch lazily.
  const allDocSets = useDocumentSets();

  // Tasks-mode KG: derive doc-set from first task; validate consistency.
  const taskDocSetId =
    mode === 'tasks' && evalTargetKind === 'KG' ? (selectedTasks[0]?.documentSetId ?? '') : '';
  const taskDocSetsInconsistent =
    mode === 'tasks' &&
    evalTargetKind === 'KG' &&
    selectedTasks.length > 1 &&
    !selectedTasks.every((t) => t.documentSetId === selectedTasks[0]?.documentSetId);

  // Effective doc-set id for the payload: tasks-derived or suite-picked.
  const effectiveDocumentSetId =
    evalTargetKind === 'KG' ? (mode === 'tasks' ? taskDocSetId : kgDocumentSetId) : '';

  // ── Payload builder ──
  const payload = useMemo((): Record<string, unknown> => {
    // MEMORY evals are self-contained (DEV-17245): no carrier workflow, and
    // the API rejects workflowVersionId on them.
    const base: Record<string, unknown> =
      evalTargetKind === 'MEMORY'
        ? {}
        : {
            workflowId: workflowId || undefined,
            ...(workflowVersionId ? { workflowVersionId } : {}),
          };
    // Ensure reportKs always has at least [1] — an empty array would
    // bypass the API default and leave the harness with no k values.
    const safeReportKs = reportKs.length > 0 ? reportKs : [1];

    // Parse comma-separated tags (server re-normalizes: trim/lower/dedupe).
    const tags = [
      ...new Set(
        tagsInput
          .split(',')
          .map((t) => t.trim().toLowerCase())
          .filter(Boolean),
      ),
    ];
    const tagFields = tags.length > 0 ? { tags } : {};

    const kgFields =
      evalTargetKind === 'KG'
        ? {
            targetKind: 'KG' as const,
            ...(effectiveDocumentSetId ? { documentSetId: effectiveDocumentSetId } : {}),
            ...(kgVersionPinId ? { documentSetKgVersionId: kgVersionPinId } : {}),
          }
        : evalTargetKind === 'MEMORY'
          ? { targetKind: 'MEMORY' as const }
          : {};

    if (mode === 'suite') {
      return {
        ...base,
        ...kgFields,
        ...tagFields,
        suiteId: suiteId || undefined,
        nTrials,
        reportKs: safeReportKs,
        datasetFilter: { concurrency },
      };
    }

    if (mode === 'tasks') {
      return {
        ...base,
        ...kgFields,
        ...tagFields,
        taskIds: selectedTaskIds.length > 0 ? selectedTaskIds : undefined,
        nTrials,
        reportKs: safeReportKs,
        datasetFilter: { concurrency },
      };
    }

    // Legacy
    const scoring: Record<string, unknown> = { evaluationMethod, mode: 'advanced' };
    if (needsGrader && graderModelConfigId) {
      scoring.llmJudge = { modelConfigId: graderModelConfigId };
    }
    return {
      ...base,
      ...tagFields,
      datasetFilter: { concurrency, scoring },
    };
  }, [
    workflowId,
    workflowVersionId,
    evalTargetKind,
    effectiveDocumentSetId,
    kgVersionPinId,
    mode,
    suiteId,
    selectedTaskIds,
    nTrials,
    reportKs,
    concurrency,
    tagsInput,
    evaluationMethod,
    needsGrader,
    graderModelConfigId,
  ]);

  // ── Validation ──
  const canSubmit = useMemo(() => {
    // Workflow is required for WORKFLOW-target evals; KG evals no longer
    // need a carrier workflow (removed the FK).
    if (evalTargetKind === 'WORKFLOW' && !workflowId) return false;

    if (mode === 'suite') {
      if (!suiteId) return false;
      // Suite is KG → need an explicit doc-set pick.
      if (evalTargetKind === 'KG' && !kgDocumentSetId) return false;
      return true;
    }

    if (mode === 'tasks') {
      if (selectedTaskIds.length === 0) return false;
      // Mixed KG/WORKFLOW tasks — block; user must split into two evals.
      if (selectedTasksMixed) return false;
      // Tasks are all KG — every task must share the same doc-set.
      if (evalTargetKind === 'KG') {
        if (!taskDocSetId) return false;
        if (taskDocSetsInconsistent) return false;
      }
      return true;
    }

    // Legacy
    return total > 0 && (!needsGrader || graderModelConfigId.length > 0);
  }, [
    workflowId,
    mode,
    suiteId,
    evalTargetKind,
    kgDocumentSetId,
    selectedTaskIds,
    selectedTasksMixed,
    taskDocSetId,
    taskDocSetsInconsistent,
    total,
    needsGrader,
    graderModelConfigId,
  ]);

  // ── Mutation ──
  const create = useMutation({
    mutationFn: () => client.http.post('v1/evaluations', { json: payload }).json<Evaluation>(),
    onSuccess: async (ev) => {
      try {
        await qc.invalidateQueries({ queryKey: ['evaluations'] });
      } catch (err) {
        console.error(err);
      }
      try {
        await nav({ to: routes.evalDetail(ev.id) });
      } catch (err) {
        console.error(err);
      }
    },
  });

  // ── Render helpers ──
  const renderModeSelector = () => (
    <div style={{ display: 'flex', gap: 'var(--sp-2)' }}>
      {(Object.keys(modeDescriptions) as EvalMode[]).map((m) => (
        <label
          key={m}
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: 4,
            padding: 'var(--sp-3)',
            background: mode === m ? 'var(--surface-2)' : 'transparent',
            border: `1px solid ${mode === m ? 'var(--accent-primary)' : 'var(--border-subtle)'}`,
            borderRadius: 'var(--radius-sm)',
            cursor: 'pointer',
            transition: 'border-color 0.15s',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input
              type="radio"
              name="eval-mode"
              value={m}
              checked={mode === m}
              onChange={() => setMode(m)}
              style={{ margin: 0 }}
            />
            <span style={{ fontWeight: 500, fontSize: 14 }}>{modeDescriptions[m].label}</span>
          </div>
          <span className="t-xs t-muted">{modeDescriptions[m].description}</span>
        </label>
      ))}
    </div>
  );

  const renderSuiteMode = () => (
    <label className="field">
      <span className="field-label">
        Suite
        <InfoHint
          text="Pick a Suite to run. All Tasks in the Suite will be evaluated against the selected workflow."
          side="right"
        />
      </span>
      <select
        required={true}
        className="select-native"
        value={suiteId}
        onChange={(e) => {
          setSuiteId(e.target.value);
          // Reset any KG doc-set pick when Suite selection changes so we
          // don't carry a stale doc-set into a non-KG Suite.
          setKgDocumentSetId('');
          setKgVersionPinId('');
        }}
      >
        <option value="">
          {suites.isLoading
            ? 'Loading suites...'
            : (suites.data?.items ?? []).length === 0
              ? '-- No suites in org --'
              : '-- Select a suite --'}
        </option>
        {(suites.data?.items ?? []).map((s) => (
          <option key={s.id} value={s.id}>
            {s.name} ({s.kind} · {s.targetKind ?? 'WORKFLOW'} · {s.tasks.length} tasks)
          </option>
        ))}
      </select>
      {selectedSuite ? (
        <span className="t-xs t-muted" style={{ marginTop: 'var(--sp-1)' }}>
          {selectedSuite.kind === 'REGRESSION' ? 'Regression suite' : 'Capability suite'}
          {' · '}
          {selectedSuite.targetKind ?? 'WORKFLOW'} target
          {' · '}
          {selectedSuite.tasks.length} {selectedSuite.tasks.length === 1 ? 'task' : 'tasks'}
          {selectedSuite.gatesRun ? ' · gates run' : ''}
        </span>
      ) : null}
    </label>
  );

  const renderTasksMode = () => (
    <div className="field">
      <span className="field-label">
        Tasks
        <InfoHint
          text="Pick individual Tasks to evaluate. Each selected Task will run nTrials times against the workflow."
          side="right"
        />
      </span>

      {/* Selected tasks as chips */}
      {selectedTasks.length > 0 ? (
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 6,
            marginBottom: 'var(--sp-2)',
          }}
        >
          {selectedTasks.map((t) => (
            <span
              key={t.id}
              className="badge"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                padding: '4px 10px',
                fontSize: 12,
                background: 'var(--surface-2)',
                border: '1px solid var(--border-subtle)',
                borderRadius: 'var(--radius-sm)',
              }}
            >
              {t.name}
              <span className="t-muted" style={{ fontSize: 11 }}>
                ({t.targetKind ?? 'WORKFLOW'})
              </span>
              <button
                type="button"
                onClick={() => setSelectedTaskIds((prev) => prev.filter((id) => id !== t.id))}
                style={{
                  border: 'none',
                  background: 'none',
                  color: 'var(--text-muted)',
                  cursor: 'pointer',
                  padding: 0,
                  fontSize: 13,
                  lineHeight: 1,
                }}
              >
                x
              </button>
            </span>
          ))}
        </div>
      ) : null}

      {/* Searchable task picker */}
      {}
      <div
        style={{ position: 'relative' }}
        onBlur={(e) => {
          // Close dropdown when focus leaves this container entirely
          if (!e.currentTarget.contains(e.relatedTarget as Node)) {
            // Small delay so the button onClick fires before we close
            setTimeout(() => setTaskPickerOpen(false), 150);
          }
        }}
      >
        <input
          type="text"
          className="input"
          placeholder={
            tasks.isLoading
              ? 'Loading tasks...'
              : availableTasks.length === 0 && selectedTaskIds.length > 0
                ? 'All tasks selected'
                : 'Search or click to browse tasks...'
          }
          value={taskSearch}
          onChange={(e) => {
            setTaskSearch(e.target.value);
            setTaskPickerOpen(true);
          }}
          onFocus={() => setTaskPickerOpen(true)}
          disabled={
            tasks.isLoading || (availableTasks.length === 0 && selectedTaskIds.length === 0)
          }
        />
        {taskPickerOpen && filteredTasks.length > 0 ? (
          <div
            style={{
              border: '1px solid var(--border-subtle)',
              borderRadius: 'var(--radius-sm)',
              maxHeight: 200,
              overflowY: 'auto',
              background: 'var(--surface-1)',
            }}
          >
            {filteredTasks.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => {
                  setSelectedTaskIds((prev) => [...prev, t.id]);
                  setTaskSearch('');
                  setTaskPickerOpen(false);
                }}
                style={{
                  display: 'block',
                  width: '100%',
                  textAlign: 'left',
                  padding: '8px 12px',
                  border: 'none',
                  background: 'none',
                  color: 'var(--text-primary)',
                  cursor: 'pointer',
                  fontSize: 13,
                  borderBottom: '1px solid var(--border-subtle)',
                }}
                onMouseEnter={(e) => {
                  (e.target as HTMLElement).style.background = 'var(--surface-2)';
                }}
                onMouseLeave={(e) => {
                  (e.target as HTMLElement).style.background = 'none';
                }}
              >
                {t.name}
                <span className="t-muted" style={{ marginLeft: 8, fontSize: 11 }}>
                  [{t.targetKind ?? 'WORKFLOW'}]
                </span>
                {t.description ? (
                  <span className="t-muted" style={{ marginLeft: 8, fontSize: 11 }}>
                    {t.description.slice(0, 60)}
                  </span>
                ) : null}
              </button>
            ))}
          </div>
        ) : null}
        {taskPickerOpen && taskSearch && filteredTasks.length === 0 ? (
          <span className="t-xs t-muted" style={{ marginTop: 'var(--sp-1)', display: 'block' }}>
            No matching tasks found.
          </span>
        ) : null}
      </div>
      <span className="t-xs t-muted" style={{ marginTop: 'var(--sp-1)' }}>
        {selectedTaskIds.length} {selectedTaskIds.length === 1 ? 'task' : 'tasks'} selected
      </span>
      {selectedTasksMixed ? (
        <span className="t-xs" style={{ color: 'var(--status-danger)' }}>
          Selection mixes KG and WORKFLOW tasks. Split them into two separate evaluations.
        </span>
      ) : null}
      {evalTargetKind === 'KG' && taskDocSetsInconsistent ? (
        <span className="t-xs" style={{ color: 'var(--status-danger)' }}>
          Selected KG tasks reference different doc-sets. Every task in a KG eval must share the
          same documentSetId.
        </span>
      ) : null}
    </div>
  );

  const renderTrialSettings = () => (
    <div
      style={{
        padding: 'var(--sp-3)',
        background: 'var(--surface-2)',
        borderRadius: 'var(--radius-sm)',
        ...stack3,
      }}
    >
      <div style={{ fontWeight: 500, fontSize: 13, marginBottom: 'var(--sp-1)' }}>
        Trial settings
      </div>
      <div style={grid2}>
        <label className="field">
          <span className="field-label">
            Trials per Task
            <InfoHint
              text="Number of independent workflow executions per Task. More trials = better reliability estimates but longer execution time."
              side="right"
            />
          </span>
          <input
            type="number"
            min={1}
            max={100}
            className="input"
            value={nTrials}
            onChange={(e) => {
              const v = Math.max(1, Number(e.target.value) || 1);
              setNTrials(v);
              // Keep reportKs synced to the full 1..nTrials range while it
              // still matches the previous full range (i.e. the user hasn't
              // hand-edited the chips). Once customized, only prune > nTrials.
              setReportKs((prev) =>
                isContiguousFromOne(prev) ? rangeKs(v) : prev.filter((k) => k <= v),
              );
            }}
          />
          <span className="t-xs t-muted">Each Trial is one independent run.</span>
        </label>
        <label className="field">
          <span className="field-label">
            Concurrency
            <InfoHint
              text="How many Tasks/Trials run in parallel. Higher = faster but more load on model providers."
              side="right"
            />
          </span>
          <input
            type="number"
            min={1}
            max={32}
            className="input"
            value={concurrency}
            onChange={(e) => setConcurrency(Math.max(1, Number(e.target.value) || 1))}
          />
          <span className="t-xs t-muted">Parallel batch size (default 4).</span>
        </label>
      </div>
      <label className="field">
        <span className="field-label">
          Report k values
          <InfoHint
            text={
              <>
                Reliability horizons for pass@k / pass^k reporting. Each k must be &le; nTrials.
                <br />
                <br />
                <b>pass@k</b>: probability that at least one of k attempts succeeds.
                <br />
                <b>pass^k</b>: probability that all k attempts succeed.
              </>
            }
            side="right"
          />
        </span>
        <ChipInput values={reportKs} onChange={setReportKs} max={nTrials} placeholder="add k" />
        <span className="t-xs t-muted">
          Each k must be &le; {nTrials} (nTrials). Type a number and press Enter.
        </span>
      </label>
      <label className="field">
        <span className="field-label">
          Tags
          <InfoHint
            text="Optional comma-separated labels (e.g. intel, regression) for grouping and filtering this eval on the Evals overview."
            side="right"
          />
        </span>
        <input
          type="text"
          className="input"
          value={tagsInput}
          onChange={(e) => setTagsInput(e.target.value)}
          placeholder="e.g. intel, weekly-regression"
        />
        <span className="t-xs t-muted">Comma-separated. Filter by these on the Evals page.</span>
      </label>
    </div>
  );

  const renderLegacyMode = () => (
    <>
      <div style={grid2}>
        <label className="field">
          <span className="field-label">
            Concurrency
            <InfoHint
              text="How many ground truth entries the harness runs in parallel. Higher = faster but more load on the runner and model providers. Defaults to 4."
              side="right"
            />
          </span>
          <input
            type="number"
            min={1}
            max={32}
            className="input"
            value={concurrency}
            onChange={(e) => setConcurrency(Math.max(1, Number(e.target.value) || 1))}
          />
          <span className="t-xs t-muted">Parallel run batch size (default 4).</span>
        </label>
        <label className="field">
          <span className="field-label">
            Scoring method
            <InfoHint
              text={
                <>
                  <b>Deterministic:</b> exact/normalized matches on output fields and JSON-path
                  assertions. Fast, cheap, reproducible.
                  <br />
                  <br />
                  <b>LLM-as-grader:</b> a separate model scores actual vs expected against a rubric.
                  Useful for free-form text where exact match is too strict.
                  <br />
                  <br />
                  <b>Both:</b> runs deterministic + grader and reports both scores per entry.
                </>
              }
              side="right"
            />
          </span>
          <select
            className="select-native"
            value={evaluationMethod}
            onChange={(e) =>
              setEvaluationMethod(e.target.value as 'deterministic' | 'llm-judge' | 'both')
            }
          >
            <option value="deterministic">Deterministic</option>
            <option value="llm-judge">LLM-as-grader</option>
            <option value="both">Both</option>
          </select>
        </label>
        {needsGrader ? (
          <label className="field">
            <span className="field-label">
              Grader model config
              <InfoHint
                text="The ModelConfig the eval harness uses for LLM-as-grader scoring. Picks from your org's models page -- credentials are decrypted server-side at run time."
                side="right"
              />
            </span>
            <select
              className="select-native"
              value={graderModelConfigId}
              onChange={(e) => setGraderModelConfigId(e.target.value)}
              required={true}
            >
              <option value="">
                {models.isLoading
                  ? 'Loading models...'
                  : (models.data?.items ?? []).length === 0
                    ? '-- No models in org --'
                    : '-- Pick a model --'}
              </option>
              {(models.data?.items ?? []).map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name} ({m.provider} · {m.modelId})
                </option>
              ))}
            </select>
            {needsGrader && !graderModelConfigId ? (
              <span className="t-xs" style={{ color: 'var(--status-danger)' }}>
                Required when scoring uses LLM-as-grader.
              </span>
            ) : null}
          </label>
        ) : null}
      </div>

      {/* GT count indicator */}
      {workflowId ? (
        <span className="t-xs t-muted" style={{ display: 'inline-flex', alignItems: 'center' }}>
          {gtCount.isLoading
            ? 'Loading ground truth...'
            : noGroundTruth
              ? 'No ground truth entries for this workflow -- add some before evaluating.'
              : `${total} ground truth ${total === 1 ? 'entry' : 'entries'} will be evaluated.`}
          <InfoHint
            text="Ground truth entries are the dataset: each one supplies an input payload plus the expected output. The harness enqueues one run per entry and scores actual vs expected."
            side="right"
          />
        </span>
      ) : null}
    </>
  );

  // ── KG-only sub-blocks (rendered when the derived kind is KG) ──

  const renderSuiteKgDocSetPicker = () => (
    <label className="field">
      <span className="field-label">
        Doc-set (KG target)
        <InfoHint
          text="The Suite you picked is a KG-target Suite. Pick the doc-set whose Knowledge Graph the harness should query. All tasks in the Suite are expected to reference this doc-set."
          side="right"
        />
      </span>
      <select
        required={true}
        className="select-native"
        value={kgDocumentSetId}
        onChange={(e) => {
          setKgDocumentSetId(e.target.value);
          setKgVersionPinId('');
        }}
      >
        <option value="">
          {allDocSets.isLoading
            ? 'Loading doc-sets…'
            : (allDocSets.data ?? []).length === 0
              ? '-- No doc-sets in org --'
              : '-- Select a doc-set --'}
        </option>
        {(allDocSets.data ?? []).map((ds) => (
          <option key={ds.id} value={ds.id}>
            {ds.name} ({ds.id.slice(0, 8)})
          </option>
        ))}
      </select>
    </label>
  );

  const renderKgVersionPin = () => (
    <details
      style={{
        background: 'var(--surface-2)',
        padding: 'var(--sp-2)',
        borderRadius: 'var(--radius-sm)',
      }}
    >
      <summary className="t-xs t-muted" style={{ cursor: 'pointer', userSelect: 'none' }}>
        Advanced: pin a specific KG version
      </summary>
      <label className="field" style={{ marginTop: 'var(--sp-2)' }}>
        <span className="field-label">
          DocumentSetKgVersion id (optional)
          <InfoHint
            text="Pin the eval to a specific KG version — typically a DRAFT candidate the refinement agent created. Leave blank to score against the doc-set's ACTIVE version."
            side="right"
          />
        </span>
        <input
          className="input"
          type="text"
          placeholder="Leave blank to use the ACTIVE version"
          value={kgVersionPinId}
          onChange={(e) => setKgVersionPinId(e.target.value.trim())}
        />
      </label>
    </details>
  );

  return (
    <>
      <PageHeader
        title="Create evaluation"
        actions={<BackButton to={routes.EVALS} label="Evaluations" />}
      />
      <div className="card" style={{ maxWidth: 780 }}>
        <div className="card-body">
          <form
            style={stack3}
            onSubmit={(e) => {
              e.preventDefault();
              if (canSubmit && !create.isPending) create.mutate();
            }}
          >
            {/* Evaluation mode selector — targetKind is derived from the
                selected Suite / Tasks, not picked manually. */}
            {renderModeSelector()}

            {/* Workflow picker — required for WORKFLOW target only.
                made Evaluation.workflowId nullable, so
                KG evals no longer need a carrier workflow. */}
            {evalTargetKind === 'WORKFLOW' && (
              <label className="field">
                <span className="field-label">
                  Workflow
                  <InfoHint
                    text="The workflow whose graph will be executed. Pick the workflow first, then optionally pin a specific version below."
                    side="right"
                  />
                </span>
                <select
                  required={true}
                  className="select-native"
                  value={workflowId}
                  onChange={(e) => {
                    setWorkflowId(e.target.value);
                    setWorkflowVersionId('');
                    setSuiteId('');
                    setSelectedTaskIds([]);
                  }}
                >
                  <option value="">-- Select a workflow --</option>
                  {(workflows.data?.items ?? []).map((w) => (
                    <option key={w.id} value={w.id}>
                      {w.name} ({w.id.slice(0, 8)})
                    </option>
                  ))}
                </select>
              </label>
            )}

            {/* Workflow version pin — only meaningful for WORKFLOW evals. */}
            {evalTargetKind === 'WORKFLOW' && workflowId ? (
              <label className="field">
                <span className="field-label">
                  Workflow version
                  <InfoHint
                    text="Optional. Defaults to the workflow's currentVersion at execution time. Pin to a specific version (e.g. an experimental DRAFT) to evaluate that snapshot without promoting it."
                    side="right"
                  />
                </span>
                <select
                  className="select-native"
                  value={workflowVersionId}
                  onChange={(e) => setWorkflowVersionId(e.target.value)}
                >
                  <option value="">-- Use current version (default) --</option>
                  {versions.isLoading ? <option disabled={true}>Loading versions...</option> : null}
                  {[...(versions.data ?? [])]
                    .sort((a, b) => b.versionNumber - a.versionNumber)
                    .map((v) => (
                      <option key={v.id} value={v.id}>
                        v{v.versionNumber} · {v.status}
                        {v.status === 'ACTIVE' ? ' (current)' : ''}
                        {' · '}
                        {v.id.slice(0, 8)}
                      </option>
                    ))}
                </select>
                {workflowVersionId ? (
                  <span className="t-xs t-muted" style={{ marginTop: 'var(--sp-1)' }}>
                    Pinned. The eval&apos;s runs will target this version regardless of which
                    version is currently ACTIVE.
                  </span>
                ) : null}
              </label>
            ) : null}

            {/* Mode-specific content */}
            {mode === 'suite' ? renderSuiteMode() : null}
            {mode === 'tasks' ? renderTasksMode() : null}
            {mode !== 'legacy' ? renderTrialSettings() : null}
            {mode === 'legacy' ? renderLegacyMode() : null}

            {/* KG doc-set picker — Suite mode only; Tasks mode derives it. */}
            {mode === 'suite' && evalTargetKind === 'KG' ? renderSuiteKgDocSetPicker() : null}

            {/* KG-eval status hint + version pin. */}
            {evalTargetKind === 'KG' ? (
              <>
                {mode === 'tasks' && taskDocSetId ? (
                  <span
                    className="t-xs t-muted"
                    style={{ display: 'inline-flex', alignItems: 'center' }}
                  >
                    Doc-set derived from selected tasks: {taskDocSetId.slice(0, 8)}
                    <InfoHint
                      text="Every KG task pins its own documentSetId. When the whole task selection targets one doc-set, we use that; if they diverge, submission is blocked above."
                      side="right"
                    />
                  </span>
                ) : null}
                {renderKgVersionPin()}
                {effectiveDocumentSetId ? (
                  <span
                    className="t-xs t-muted"
                    style={{ display: 'inline-flex', alignItems: 'center' }}
                  >
                    KG evaluation. Scored by kg_answer_cited_recall. Entity/relation graders land in
                    Track B.
                  </span>
                ) : null}
              </>
            ) : null}

            {/* Request body preview */}
            <details
              style={{
                background: 'var(--surface-2)',
                padding: 'var(--sp-2)',
                borderRadius: 'var(--radius-sm)',
              }}
            >
              <summary className="t-xs t-muted" style={{ cursor: 'pointer', userSelect: 'none' }}>
                Preview request body
                <InfoHint
                  text="The exact JSON that will be POSTed to /v1/evaluations."
                  side="right"
                />
              </summary>
              <pre
                className="t-mono"
                style={{ fontSize: 11, marginTop: 'var(--sp-2)', overflow: 'auto' }}
              >
                {JSON.stringify(
                  {
                    ...payload,
                    workflowId: (payload.workflowId as string) || '<select a workflow>',
                  },
                  null,
                  2,
                )}
              </pre>
            </details>

            {/* Submit */}
            <div style={{ display: 'flex', gap: 'var(--sp-2)', alignItems: 'center' }}>
              <button
                type="submit"
                className="btn btn--primary"
                disabled={!canSubmit || create.isPending}
              >
                {create.isPending ? 'Creating...' : 'Create evaluation'}
              </button>
              {create.error ? (
                <span className="t-xs" style={{ color: 'var(--status-danger)' }}>
                  {(create.error as Error).message}
                </span>
              ) : null}
              {mode === 'legacy' && noGroundTruth ? (
                <Link
                  to={routes.GROUND_TRUTH_NEW}
                  className="t-xs"
                  style={{ color: 'var(--accent-primary)' }}
                >
                  → Add ground truth for this workflow
                </Link>
              ) : null}
              {mode === 'suite' && suites.data && suites.data.items.length === 0 ? (
                <Link
                  to={routes.SUITES_NEW}
                  className="t-xs"
                  style={{ color: 'var(--accent-primary)' }}
                >
                  → Create a suite first
                </Link>
              ) : null}
              {mode === 'tasks' && tasks.data && taskList.length === 0 ? (
                <Link
                  to={routes.TASKS_NEW}
                  className="t-xs"
                  style={{ color: 'var(--accent-primary)' }}
                >
                  → Create a task first
                </Link>
              ) : null}
            </div>
          </form>
        </div>
      </div>
    </>
  );
}
